

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const CALLBACK_NAME = "__iattInitGoogleMaps";
const PLACE_FIELDS = ["displayName", "formattedAddress", "location"];

let googleMapsPromise = null;

export const loadGoogleMaps = () => {
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (googleMapsPromise) return googleMapsPromise;

  if (!GOOGLE_MAPS_API_KEY) {
    return Promise.reject(
      new Error(
        "Google Maps API key is missing. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env",
      ),
    );
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    window[CALLBACK_NAME] = () => {
      delete window[CALLBACK_NAME];
      resolve(window.google.maps);
    };

    // Google calls this when the key is invalid / not authorised for this domain
    window.gm_authFailure = () => {
      console.error("Google Maps authentication failed. Check the API key and its restrictions.");
    };

    const script = document.createElement("script");
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}` +
      `&libraries=places&loading=async&callback=${CALLBACK_NAME}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      googleMapsPromise = null;
      script.remove();
      reject(new Error("Failed to load Google Maps"));
    };

    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

export const getShortAddress = (result) => {
  const components = result?.address_components || [];
  const getComponent = (type) =>
    components.find((item) => item.types.includes(type))?.long_name || "";

  const area =
    getComponent("sublocality_level_2") ||
    getComponent("sublocality_level_1");
  const city = getComponent("locality");

  return [area, city].filter(Boolean).join(", ");
};

// Resolve the captured coordinates to Google's closest available address.
export const reverseGeocode = async (latitude, longitude) => {
  let timeoutId;
  try {
    return await Promise.race([
      (async () => {
        const maps = await loadGoogleMaps();
        const { Geocoder } = await maps.importLibrary("geocoding");
        const { results } = await new Geocoder().geocode({
          location: { lat: latitude, lng: longitude },
        });
        if (!results?.length) return null;

        const result = results[0];
        return {
          fullAddress: result.formatted_address,
          shortAddress: getShortAddress(result),
          placeId: result.place_id,
        };
      })(),
      new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error("Location address lookup timed out")),
          15000,
        );
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const loadPlaces = async () => {
  const maps = await loadGoogleMaps();
  return maps.importLibrary("places");
};

const toResult = (place) =>
  place && {
    lat: place.location?.lat(),
    lng: place.location?.lng(),
    name: place.formattedAddress || place.displayName || "",
  };

// Name of the nearest place to a coordinate (Places API New - Nearby Search), or "" if none
export const getPlaceNameAt = async (lat, lng) => {
  const { Place, SearchNearbyRankPreference } = await loadPlaces();
  const { places } = await Place.searchNearby({
    fields: PLACE_FIELDS,
    locationRestriction: {
      center: { lat: Number(lat), lng: Number(lng) },
      radius: 50,
    },
    maxResultCount: 1,
    rankPreference: SearchNearbyRankPreference.DISTANCE,
  });
  return toResult(places?.[0])?.name || "";
};

// Details of a place clicked on the map (POI icon), or null
export const getPlaceById = async (placeId) => {
  const { Place } = await loadPlaces();
  const place = new Place({ id: placeId });
  await place.fetchFields({ fields: PLACE_FIELDS });
  return toResult(place);
};

// New autocomplete session; pass it to every suggestion request until a place is picked
export const createPlacesSession = async () => {
  const { AutocompleteSessionToken } = await loadPlaces();
  return new AutocompleteSessionToken();
};

// Suggestions for text typed so far (Places API New - Autocomplete)
export const getPlaceSuggestions = async (input, sessionToken) => {
  const { AutocompleteSuggestion } = await loadPlaces();
  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input,
      sessionToken,
    });
  return suggestions
    .map((s) => s.placePrediction)
    .filter(Boolean)
    .map((prediction) => ({
      placeId: prediction.placeId,
      text: prediction.text?.text || "",
      mainText: prediction.mainText?.text || prediction.text?.text || "",
      secondaryText: prediction.secondaryText?.text || "",
      prediction,
    }));
};

// Coordinates of a picked suggestion (ends the autocomplete session), or null
export const getSuggestionDetails = async (suggestion) => {
  const place = suggestion.prediction.toPlace();
  await place.fetchFields({ fields: PLACE_FIELDS });
  const result = toResult(place);
  return result && { ...result, name: suggestion.text || result.name };
};
